import React, { useState, useMemo } from 'react';
import {
  View,
  StyleSheet,
  Platform,
  Dimensions,
  Pressable,
  Alert,
  Image,
} from 'react-native';
import { useTheme } from '../../hooks/useTheme';
import { useAuth } from '../../hooks/useAuth';
import InviteModal from './InviteModal';
import UserMenu from './UserMenu';
import { invitesAPI } from '../../api/invites';
import { logger } from '../../utils/logger';

interface TopNavigationBarProps {
  onProfilePress?: () => void;
  onSettingsPress?: () => void;
  onInvitePress?: () => void;
  onLogoutPress?: () => void;
  onLogoPress?: () => void;
  pageTitle?: string;
  hideActions?: boolean;
}

const TopNavigationBar: React.FC<TopNavigationBarProps> = ({
  onProfilePress,
  onSettingsPress,
  onInvitePress,
  onLogoutPress,
  onLogoPress,
  hideActions = false,
}) => {
  const { colors } = useTheme();
  const { user } = useAuth();
  const [showInviteModal, setShowInviteModal] = useState(false);

  const screenWidth = Dimensions.get('window').width;
  const isMobile = Platform.OS !== 'web' || screenWidth < 768;

  // Generate invite code based on user ID
  const inviteCode = useMemo(() => {
    if (!user?.id) return '';
    // Simple hash-based code generation
    const generateCode = (input: string): string => {
      let hash = 0;
      for (let i = 0; i < input.length; i++) {
        const char = input.charCodeAt(i);
        hash = (hash << 5) - hash + char;
        hash = hash & hash; // Convert to 32bit integer
      }
      // Convert to base36 and pad with user ID
      const hashStr = Math.abs(hash).toString(36).toUpperCase();
      const userIdStr = user.id.toString().padStart(4, '0');
      // Combine and take first 12 characters
      return (hashStr + userIdStr).substring(0, 12);
    };
    const input = `invite_${user.id}_${user.email || 'user'}`;
    return generateCode(input);
  }, [user?.id, user?.email]);

  // Generate invite link
  const inviteLink = useMemo(() => {
    if (!inviteCode) return '';
    const baseUrl =
      Platform.OS === 'web' && typeof window !== 'undefined'
        ? window.location.origin
        : 'https://mycoloursense.com';
    return `${baseUrl}/invite/${inviteCode}`;
  }, [inviteCode]);

  const handleProfilePress = () => {
    if (onProfilePress) onProfilePress();
  };

  const handleSettingsPress = () => {
    if (onSettingsPress) onSettingsPress();
  };

  const handleInvitePress = async () => {
    if (onInvitePress) {
      onInvitePress();
    } else if (user && inviteLink && inviteCode) {
      try {
        await invitesAPI.create(inviteCode);
      } catch (error: unknown) {
        const err = error as Error;
        if (err.message?.includes('already exists')) {
          logger.info('Invite code already exists in backend');
        } else {
          logger.error('Failed to save invite code:', err);
        }
      }

      setShowInviteModal(true);
    } else {
      Alert.alert('Invite Friends', 'Please log in to invite friends!');
    }
  };

  const handleLogoutPress = () => {
    if (onLogoutPress) {
      onLogoutPress();
    } else {
      Alert.alert('Logout', 'Logout functionality will be implemented soon!');
    }
  };

  const handleLogoPress = () => {
    if (onLogoPress) {
      onLogoPress();
    }
  };

  // Use panelBackground so the bar matches the content area (one soft surface); avoids stark white strip
  const barBackground = colors.panelBackground ?? colors.background;

  return (
    <View
      style={[
        styles.container,
        {
          backgroundColor: barBackground,
          borderBottomColor: colors.border,
        },
        isMobile && styles.mobileContainer,
      ]}
    >
      <View style={[styles.contentWrapper, isMobile && styles.mobileGradient]}>
        <View style={styles.content}>
          {/* Left: logo only — page title lives in the page (hero/H1) for modern minimal chrome */}
          <View style={styles.leftSection}>
            <Pressable
              onPress={handleLogoPress}
              style={({ pressed }) => [
                styles.logoButton,
                {
                  backgroundColor: pressed
                    ? 'rgba(255, 255, 255, 0.1)'
                    : 'transparent',
                },
              ]}
            >
              <Image
                source={require('../../../assets/icon.png')}
                style={styles.logoIcon}
                resizeMode="contain"
                fadeDuration={0}
              />
            </Pressable>
          </View>

          {/* Right: profile menu (avatar → dropdown with View profile, Settings, Invite, Logout) */}
          <View style={styles.rightSection}>
            {!hideActions && user && (
              <UserMenu
                onProfilePress={handleProfilePress}
                onSettingsPress={handleSettingsPress}
                onInvitePress={handleInvitePress}
                onLogout={handleLogoutPress}
              />
            )}
          </View>
        </View>
      </View>

      {/* Invite Modal */}
      <InviteModal
        visible={showInviteModal}
        onClose={() => setShowInviteModal(false)}
        inviteLink={inviteLink}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    height: 60,
    width: '100%',
    borderBottomWidth: 1,
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
    zIndex: 1000,
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
  },
  mobileContainer: {
    borderBottomWidth: 0,
    borderRadius: 16,
    marginHorizontal: 16,
    marginTop: 8,
    left: 16,
    right: 16,
    width: undefined,
    overflow: 'hidden',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.15,
    shadowRadius: 8,
    elevation: 5,
  },
  contentWrapper: {
    flex: 1,
    width: '100%',
  },
  mobileGradient: {
    borderRadius: 16,
  },
  content: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingLeft: 8,
    paddingRight: 24,
    paddingVertical: 12,
  },
  leftSection: {
    flex: 1,
    alignItems: 'flex-start',
  },
  logoButton: {
    padding: 8,
    borderRadius: 8,
    transition: 'all 0.2s ease',
  },
  logoIcon: {
    height: 56,
    width: 56,
  },
  logoWithName: {
    height: 40,
    width: 180,
  },
  rightSection: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 2,
  },
  actionButton: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 8,
    transition: 'all 0.2s ease',
    minWidth: 40,
    minHeight: 40,
  },
  buttonLabel: {
    fontSize: 14,
    fontWeight: '500',
  },
});

export default TopNavigationBar;
